/**
 * ============================================
 * MASTER NAVIGATION TOGGLE
 * ============================================
 * 
 * To show/hide navigation items, edit this file:
 * frontend/src/utils/featureFlags.ts
 * 
 * Find: FEATURE_FLAGS.showAllNavItems
 * 
 * - Set to TRUE: Shows all navigation items
 * - Set to FALSE: Shows only Blog Agents, Kits, and Knowledge Base
 * 
 * ============================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import './App.css';
import Navigation from './components/Navigation';
import UpdateNotificationSystem from './components/UpdateNotificationSystem';
import LandingPage from './pages/LandingPage';
import AppPage from './pages/AppPage';
import CROPage from './pages/CROPage';
import PlaybooksPage from './pages/PlaybooksPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import BrandKitPage from './pages/BrandKitPage';
import VoCKitPage from './pages/VoCKitPage';
import VoiceOfCustomerAgentPage from './pages/VoiceOfCustomerAgentPage';
import CTACreatorPage from './pages/CTACreatorPage';
import BlogCreatorPage from './pages/BlogCreatorPage';
import GongAnalysisPage from './pages/GongAnalysisPage';
import LandingPageAnalyzer from './pages/LandingPageAnalyzer';
import BlogHistoryPage from './pages/BlogHistoryPage';
import CompetitorConquestingPage from './pages/CompetitorConquestingPage';
import RewriteBlogPostsPage from './pages/RewriteBlogPostsPage';
import PeopleAlsoAskPage from './pages/PeopleAlsoAskPage';
import SitemapPage from './pages/SitemapPage';
import SitemapPageEnhanced from './pages/SitemapPageEnhanced';
import SitemapDetailPage from './pages/SitemapDetailPage';
import UncoverPage from './pages/UncoverPage';
import EmailNewsletterPage from './pages/EmailNewsletterPage';
import MDSEmailTemplatesPage from './pages/MDSEmailTemplatesPage';
import QuizPage from './pages/QuizPage';
import CitrusDoctorPage from './pages/CitrusDoctorPage';
import NewTopicsAgentPage from './pages/NewTopicsAgentPage';
import CKAAArticlesAgentPage from './pages/CKAAArticlesAgentPage';
import PSBArticlesAgentPage from './pages/PSBArticlesAgentPage';
import SSArticlesAgentPage from './pages/SSArticlesAgentPage';
import TOFUArticlesAgentPage from './pages/TOFUArticlesAgentPage';
import BestOfArticlesAgentPage from './pages/BestOfArticlesAgentPage';
import { FEATURE_FLAGS } from './utils/featureFlags';
import { BrandProvider, useBrand } from './context/BrandContext';
import { DEFAULT_BRAND_KEY, isBrandKey } from './config/brands';
import { getBrandPath, toDefaultBrandPath } from './utils/brandRouting';

const AppLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { brand, getPath } = useBrand();
  // Why this matters: Track timeout ID to prevent memory leaks on unmount
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Handle body scroll lock when mobile menu is open
   * Why this matters: Prevents background scrolling when mobile menu overlay is active
   */
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    // Cleanup on unmount - clear timeout and body class
    return () => {
      document.body.classList.remove('mobile-menu-open');
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isMobileMenuOpen]);

  /**
   * Toggle mobile menu visibility
   * Why this matters: Provides mobile users with access to navigation in a standard slide-in pattern
   */
  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    } else {
      setIsMobileMenuOpen(true);
      setIsClosing(false);
    }
  };


  /**
   * Close mobile menu with animation
   * Why this matters: Provides smooth exit animation for better UX
   * Uses ref to track timeout so it can be cleared on unmount (prevents memory leaks)
   */
  const closeMobileMenu = () => {
    setIsClosing(true);
    // Clear any existing timeout before setting a new one
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    // Wait for animation to complete before hiding
    closeTimeoutRef.current = setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 300); // Match animation duration
  };



  return (
    <div className="app-container">
      {/* Mobile Header - Only visible on mobile devices */}
      {/* Why this matters: Provides mobile users with a header containing logo and hamburger menu */}
      <header className="mobile-app-header">
        <Link to={getPath()} className="mobile-logo-link">
          {brand.logoSrc ? (
            <img
              src={brand.logoSrc}
              alt={brand.displayName}
              className="mobile-header-logo"
            />
          ) : (
            <span className="mobile-header-brand-name">{brand.domain}</span>
          )}
        </Link>
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu Overlay - Slides in from right when hamburger is clicked */}
      {/* Why this matters: Shows navigation as full-screen overlay on mobile */}
      {isMobileMenuOpen && (
        <div 
          className={`mobile-menu-overlay ${isClosing ? 'closing' : ''}`}
          onClick={closeMobileMenu}
        >
          <div 
            className={`mobile-menu-content ${isClosing ? 'closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Navigation onItemClick={closeMobileMenu} />
          </div>
        </div>
      )}

      {/* Main Layout - Full viewport without header */}
      <div className="main-layout sidebar-expanded">
        {/* Left Navigation - Desktop only, hidden on mobile via CSS */}
        <div className="navigation-container">
          <Navigation />
        </div>

        {/* Page Content */}
        <div className="page-content">
          <Outlet />
        </div>
      </div>

      {/* Update Notification System - appears on all pages */}
      <UpdateNotificationSystem 
        enabled={true}
        checkInterval={5 * 60 * 1000} // 5 minutes
        maxCommitsToShow={5}
      />
    </div>
  );
};

const BrandedRoutes: React.FC = () => {
  const { brandKey } = useParams<{ brandKey: string }>();

  if (!isBrandKey(brandKey)) {
    return <Navigate to={getBrandPath(DEFAULT_BRAND_KEY)} replace />;
  }

  if (!FEATURE_FLAGS.brandAvailability[brandKey]) {
    return <Navigate to={getBrandPath(DEFAULT_BRAND_KEY)} replace />;
  }

  const brandNavVisibility = FEATURE_FLAGS.brandNavigationVisibility[brandKey];

  return (
    <BrandProvider brandKey={brandKey}>
      <Routes>
        <Route index element={<LandingPage />} />
        <Route path="quiz" element={<QuizPage />} />
        <Route path="citrus-doctor" element={<CitrusDoctorPage />} />
        <Route element={<AppLayout />}>
          {/* Reddit Agents Routes - Hidden when showAllNavItems is false */}
          {FEATURE_FLAGS.showAllNavItems && <Route path="app" element={<AppPage />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="uncover" element={<UncoverPage />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="reddit-analysis-history" element={<HistoryPage />} />}

          {/* Other conditional routes */}
          {FEATURE_FLAGS.showCRO && <Route path="cro" element={<CROPage />} />}
          {FEATURE_FLAGS.showLandingPageAnalyzer && <Route path="landing-page-analyzer" element={<LandingPageAnalyzer />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="gong-analysis" element={<GongAnalysisPage />} />}

          {/* Lifecycle Agents Routes - Hidden when showAllNavItems is false */}
          {FEATURE_FLAGS.showAllNavItems && <Route path="lifecycle-agents" element={<EmailNewsletterPage />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="mds-email-templates" element={<MDSEmailTemplatesPage />} />}

          {/* Other hideable routes */}
          {FEATURE_FLAGS.showAllNavItems && <Route path="voice-of-customer-agent" element={<VoiceOfCustomerAgentPage />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="cta-creator" element={<CTACreatorPage />} />}
          {FEATURE_FLAGS.showAllNavItems && <Route path="playbooks-creator" element={<PlaybooksPage />} />}

          {/* Blog Agents Routes - ALWAYS VISIBLE */}
          <Route path="create-tree-articles" element={<BlogCreatorPage />} />
          {brandNavVisibility.showRewriteBlogPosts && <Route path="rewrite-blog-posts" element={<RewriteBlogPostsPage />} />}
          {brandNavVisibility.showPeopleAlsoAskAgent && <Route path="people-also-ask-agent" element={<PeopleAlsoAskPage />} />}
          <Route path="competitor-conquesting" element={<CompetitorConquestingPage />} />
          {brandNavVisibility.showNewTopicsAgent && <Route path="new-topics-agent" element={<NewTopicsAgentPage />} />}
          <Route path="ckaa-articles-agent" element={<CKAAArticlesAgentPage />} />
          <Route path="psb-articles-agent" element={<PSBArticlesAgentPage />} />
          <Route path="ss-articles-agent" element={<SSArticlesAgentPage />} />
          <Route path="tofu-articles-agent" element={<TOFUArticlesAgentPage />} />
          {brandNavVisibility.showBestOfArticlesAgent && <Route path="best-of-articles-agent" element={<BestOfArticlesAgentPage />} />}
          <Route path="blog-history" element={<BlogHistoryPage />} />

          {/* Kits Routes - ALWAYS VISIBLE */}
          <Route path="brand-kit" element={<BrandKitPage />} />
          {FEATURE_FLAGS.showVoCKit && <Route path="voc-kit" element={<VoCKitPage />} />}

          {/* Knowledge Base Routes - ALWAYS VISIBLE */}
          <Route path="knowledge-base/sitemap" element={<SitemapPageEnhanced />} />
          <Route path="knowledge-base/sitemap-old" element={<SitemapPage />} />
          <Route path="knowledge-base/sitemap/details/:sitemapId" element={<SitemapDetailPage />} />

          {/* Settings - Hidden when showAllNavItems is false */}
          {FEATURE_FLAGS.showAllNavItems && <Route path="settings" element={<SettingsPage />} />}
        </Route>
      </Routes>
    </BrandProvider>
  );
};

const LegacyRouteRedirect: React.FC = () => {
  const location = useLocation();
  const targetPath = toDefaultBrandPath(location.pathname);

  return <Navigate to={targetPath} replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to={getBrandPath(DEFAULT_BRAND_KEY)} replace />} />
        <Route path="/brands/:brandKey/*" element={<BrandedRoutes />} />
        <Route path="*" element={<LegacyRouteRedirect />} />
      </Routes>
    </Router>
  );
};

export default App;
